import React, { Component } from 'react'
import includes from 'lodash/includes'
import omit from 'lodash/omit'
import pick from 'lodash/pick'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { Field, reduxForm } from 'redux-form'
import { injectStripe } from 'react-stripe-elements'
import moment from 'moment'
import ReactPixel from 'react-facebook-pixel'
import applicationConstants from '../../../../utils/constants/application'
import history from '../../../../utils/routing/history'
import CardTitle from '../../../../components/ui/typography/CardTitle'
import PaymentDisclaimer from '../../../../components/ui/typography/PaymentDisclaimer'
import * as stripeCardElements from '../../../../components/ui/forms/renderStripeCardElementInput'
import renderTypeableDatePickerInput from '../../../../components/ui/forms/renderTypableDatePickerInput'
import renderSelectInput from '../../../../components/ui/forms/renderSelectInput'
import SelectInputWithSuggestions from '../../../../components/ui/forms/SelectInputWithSuggestions'
import renderCheckbox from '../../../../components/ui/forms/renderCheckbox'
import formValidators from '../../../../utils/validation/formValidators'
import accountUtils from '../../../../utils/account/accountUtils'
import notificationsUtils from '../../../../utils/notifications/notificationsUtils'
import selectors from '../../../../store/shared/selectors'
import * as currentUserDuck from '../../../../store/shared/currentUserDuck'
import intercom from '../../../../utils/intercom/intercomUtils'
import * as userProfileDuck from '../../../authenticated/UserProfile/userProfileDuck'
import * as paymentDuck from '../paymentDuck'
import * as insTokenDuck from '../insTokenDuck'

//cc images
import * as amex from '../../../../assets/onboarding/american-express.png'
import * as visa from '../../../../assets/onboarding/visa.png'
import * as discover from '../../../../assets/onboarding/discover.png'
import * as mastercard from '../../../../assets/onboarding/mastercard.png'

/**
 * Form Field Generator
 * @param params
 */
const createFormField = params => params.allowed && <Field {...omit(params, 'allowed')} />

/**
 * @class ProfileOnboardingForm
 * @extends Component
 */
class ProfileOnboardingForm extends Component {
  static propTypes = {
    formValues: PropTypes.object,
    handleSubmit: PropTypes.func.isRequired,
    insToken: PropTypes.string,
    profileOnboardingForm: PropTypes.any,
    selectedPlan: PropTypes.oneOf(Object.keys(applicationConstants.stripe_plan_ids).map(key => applicationConstants.stripe_plan_ids[key])),
    submitting: PropTypes.bool.isRequired,
  }

  static defaultProps = {
    profileOnboardingForm: {
      values: {},
    },
  }

  constructor(props) {
    super(props)

    this.state = {
      collegeAndUniversities: [],
    }
  }

  componentWillMount() {
    if(this.state.collegeAndUniversities.length === 0){
      import('../../../../assets/CollegeAndUniversities/collegeAndUniversities.json').then(data => this.setState({collegeAndUniversities: data}))
    }
  }

  render() {
    const {
      formValues,
      handleSubmit,
      insToken,
      selectedPlan,
      submitting,
    } = this.props

    const { collegeAndUniversities } = this.state

    const mustPerformPayment = !insToken

    // Determine which plan the user has chosen based on insToken or selectedPlan
    const plan = accountUtils.getPlanByInsTokenAndSelectedPlan(insToken, selectedPlan)

    // Get options for the userTypes based on their desired plan
    const possibleUserTypes = accountUtils.getUserTypeOptionsForPlan(plan)

    // Allowed field names based on current userType
    const allowedFieldNames = accountUtils.getAllowedFieldNamesForUserType(
      formValues.userType,
    )

    return (
      <div alt="credit card">
        <div className="row">
          <div className="col-md-6 mt-3" id="profile-info-card">
            <CardTitle content="Basic Info" />
            <div className="card shadow">
              <div className="card-body">
                {createFormField({
                  allowed: true,
                  component: renderSelectInput,
                  disabled: submitting,
                  label: 'Account Type',
                  name: 'userType',
                  options: possibleUserTypes,
                })}

                {createFormField({
                  allowed: includes(allowedFieldNames, 'dob'),
                  component: renderTypeableDatePickerInput,
                  disabled: submitting,
                  label: 'Birthday',
                  name: 'dob',
                })}

                {createFormField({
                  allowed: includes(allowedFieldNames, 'classGrade'),
                  component: renderSelectInput,
                  disabled: submitting,
                  label: 'Class Grade',
                  name: 'classGrade',
                  options: accountUtils.getClassGradeOptions(formValues.userType),
                })}

                {createFormField({
                  allowed: includes(allowedFieldNames, 'gender'),
                  component: renderSelectInput,
                  disabled: submitting,
                  label: 'Gender',
                  name: 'gender',
                  options: accountUtils.getGenderOptions(),
                })}

                {createFormField({
                  allowed: includes(allowedFieldNames, 'collegeOrUniversity'),
                  component: SelectInputWithSuggestions,
                  disabled: submitting,
                  label: 'College / University',
                  name: 'collegeOrUniversity',
                  placeholder: 'University of Georgia',
                  suggestions: collegeAndUniversities,
                })}
              </div>
            </div>
          </div>
          <div className="col-md-6 mt-3" id="profile-payment-card">
            {mustPerformPayment ? <h6 className="text-muted"><i className="fal fa-lock"/> Secure Payment</h6> : <h6>&nbsp;</h6>}
            {mustPerformPayment && (
              <div className="card shadow">
                <div className="card-body">
                  <PaymentDisclaimer />
                  <div style={{ marginBottom: '10px' }}>
                    {createFormField({
                      allowed: true,
                      component: stripeCardElements.renderStripeCardNumber,
                      disabled: submitting,
                      name: '_cardNumberReady',
                    })}
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <div className="row">
                      <div className="col">
                        {createFormField({
                          allowed: true,
                          component: stripeCardElements.renderStripeCardExpiry,
                          disabled: submitting,
                          name: '_cardExpiryReady',
                        })}
                      </div>
                      <div className="col">
                        {createFormField({
                          allowed: true,
                          component: stripeCardElements.renderStripeCardCVC,
                          disabled: submitting,
                          name: '_cardCVCReady',
                        })}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <div className="row">
                      <div className="col">
                        {createFormField({
                          allowed: true,
                          component: stripeCardElements.renderStripePostalCode,
                          disabled: submitting,
                          name: '_cardPostalCodeReady',
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="text-center" style={{marginTop: -10}}>
                    <img alt="Visa" src={visa}/>&nbsp;
                    <img alt="MasterCard" src={mastercard}/>&nbsp;
                    <img alt="American Express" src={amex}/>&nbsp;
                    <img alt="Discover" src={discover}/>
                  </div>
                  <div className="text-right">
                    <div style={{marginTop: 10, marginBottom: 0}}>
                      <h5 className="mb-0">Total Charge:</h5>
                      <h2 className="mb-0">
                        {selectedPlan === applicationConstants.stripe_plan_ids.INDIVIDUAL?
                        <div>
                          ${applicationConstants.stripe_plan_amounts.INDIVIDUAL}<sup>.00</sup>
                          <p className="mb-0 text-muted text-center" style={{fontSize: 11}}>Free Trial. Cancel anytime. First charge on {moment().add(14, 'days').calendar()}. </p>
                        </div>

                        :
                        <span>
                          ${applicationConstants.stripe_plan_amounts.PARENT}<sup>.00</sup>
                          <div className="text-muted text-center" style={{fontSize: 11}}>
                            Free Trial. Cancel anytime. First charge on {moment().add(14, 'days').calendar()}.
                            <br/>
                            Includes access for up to 3 children
                          </div>
                        </span>

                        }</h2>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div style={{ marginLeft: 10 }}>
              <Field
                component={renderCheckbox}
                label={
                  [
                    'I agree to the ',
                    <a key={1} href="https://www.steppingblocks.com/terms" target="_blank" rel="noopener noreferrer">Terms & Conditions of Service</a>,
                    ' and have read the ',
                    <a key={2} href="https://www.steppingblocks.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>,
                    '.'
                  ]
                }
                name="termsAndConditions"
              />
            </div>
            { submitting ? <div className="text-center"> Loading... </div>
              : <button
                  className="btn btn-lg btn-block btn-success"
                  onClick={handleSubmit}
                  style={{ marginTop: '20px' }}
                  id="profile-join-steppingblocks-button"
                >
                  {mustPerformPayment ?
                    (<span><i className="fal fa-lock"/> Start my free trial</span>)
                    :
                    ('Continue to Steppingblocks!')
                  }
                </button>
            }
          </div>
        </div>
      </div>
    )
  }
}

/**
 * @const validate
 * @param values Form values to check
 * @returns {{}}
 */
const validate = values => {
  const errors = {}

  errors.userType = formValidators.isRequired(values.userType)
  errors.dob = formValidators.isValidBirthDate(values.dob)
  errors.jobTitle = formValidators.isRequired(values.jobTitle)
  errors.whatToStudy = formValidators.isRequired(values.whatToStudy)
  errors.classGrade = formValidators.isRequired(values.classGrade)
  errors.gender = formValidators.isRequired(values.gender)
  errors._cardNumberReady = formValidators.isRequired(values._cardNumberReady)
  errors._cardExpiryReady = formValidators.isRequired(values._cardExpiryReady)
  errors._cardCVCReady = formValidators.isRequired(values._cardCVCReady)
  errors._cardPostalCodeReady = formValidators.isRequired(values._cardPostalCodeReady)
  errors.termsAndConditions = values.termsAndConditions ? null : 'Please accept our term and conditions'

  return errors
}

/**
 * @const wrappedWithForm
 * @description Form decorated with reduxForm props
 */
const wrappedWithForm = reduxForm({
  form: 'profileOnboardingForm',
  onSubmit: async (values, _dispatch, props) => {
    try {
      const allowedValues = pick(
        values,
        Object.keys(props.profileOnboardingForm.registeredFields),
      )

      if (!props.insToken) {
        // Creates a Stripe Credit Card Token with the user's Name attached
        const { token } = await props.stripe.createToken({ name: props.username })

        await props.performSteppingblocksPayment({
          stripe_card_token: token.id,
          stripe_plan_id: props.selectedPlan,
        })
      } else {
        allowedValues.insToken = props.insToken
      }

      const { user } = await props.performUpdateUserProfile(allowedValues)
      props.currentUserSuccess(user)

      intercom.intercomUpdate({
        user_id: user._id,
        email: user.email,
        createdAt: user.createdAt,
        updatedAt: user.updatedAt,
        insToken: user.insToken,
        stripe_needs_updated_payment: user.stripe_needs_updated_payment,
        stripe_plan_id_before_cancellation: user.stripe_plan_id_before_cancellation,
        ...user.info,
        ...user.profile,
        lastLogin: user.lastLogin,
        accessAllowed: user.accessAllowed,
        isSystemAdministrator: user.isSystemAdministrator,
      })

      let amount = 0
      switch(props.selectedPlan) {
        case applicationConstants.stripe_plan_ids.INDIVIDUAL:
          amount = applicationConstants.stripe_plan_amounts.INDIVIDUAL
          break
        case applicationConstants.stripe_plan_ids.PARENT:
          amount = applicationConstants.stripe_plan_amounts.PARENT
          break
        default:
          amount = 0
      }
      ReactPixel.track('Purchase', { value: amount, currency: 'USD' } )

      switch(user.info.userType) {
        case 'admin':
          history.push('/app/admin')
          break
        case 'parent':
          history.push('/app/parent')
          break
        default:
          history.push('/app/dashboard')
      }

    } catch (error) {
      notificationsUtils.showErrorNotification(error.message)
    }
  },
  validate,
})(ProfileOnboardingForm)

/**
 * @const makeMapStateToProps
 */
const makeMapStateToProps = () => {
  const isPerformingRequest = selectors.makeGetIsPerformingRequest()
  const getFormValues = selectors.makeGetFormValues()
  return state => ({
    insToken: insTokenDuck.getValidatedInsToken(state),
    isPerformingCurrentUserRequest: isPerformingRequest(state, 'currentUser'),
    isPerformingPaymentRequest: isPerformingRequest(state, 'payment'),
    isPerformingUserProfileRequest: isPerformingRequest(state, 'userProfile'),
    formValues: getFormValues(state.form.profileOnboardingForm),
    profileOnboardingForm: state.form.profileOnboardingForm,
    username: state.currentUser.user.profile.name
  })
}

/**
 * @const mapDispatchToProps
 * @description Bind actions to props
 * @param dispatch
 */
const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      currentUserSuccess: currentUserDuck.currentUserSuccess,
      performSteppingblocksPayment: paymentDuck.performSteppingblocksPayment,
      performUpdateUserProfile: userProfileDuck.performUpdateUserProfile,
    },
    dispatch,
  )

/**
 * @description Inject stripe provider, connect to Redux
 */
export default injectStripe(
  connect(makeMapStateToProps, mapDispatchToProps)(wrappedWithForm),
)



// WEBPACK FOOTER //
// ./src/routes/unauthenticated/Onboarding/components/ProfileOnboardingForm.js